import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Crown, Bell, Check, Lock } from "lucide-react";
import { api, formatApiError, imgUrl } from "@/lib/api";
import { toast } from "sonner";
import ChronicleInteractions from "@/components/ChronicleInteractions";
import ShareButtons from "@/components/ShareButtons";

const GATE_KEY = "nc_chronicles_unlocked";

// Render a paragraph string, honoring **bold** markers and single-newline breaks.
function renderParagraph(text, keyBase) {
  const lines = text.split("\n");
  return lines.map((line, li) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    return (
      <span key={`${keyBase}-l${li}`}>
        {parts.map((p, pi) =>
          p.startsWith("**") && p.endsWith("**") ? (
            <strong key={pi} style={{ color: "var(--gold)" }} className="font-medium">
              {p.slice(2, -2)}
            </strong>
          ) : (
            <span key={pi}>{p}</span>
          )
        )}
        {li < lines.length - 1 && <br />}
      </span>
    );
  });
}

// Build an ordered list of render blocks: text paragraphs with images
// interleaved at roughly even intervals through the story.
function buildBlocks(content, images) {
  const paragraphs = content
    .split("\n\n")
    .map((p) => p.trim())
    .filter(Boolean);
  const imgs = images || [];
  const blocks = [];
  // Image insertion points: spread images across the body, skipping the very top.
  const points = imgs.map((_, i) =>
    Math.max(1, Math.round((paragraphs.length * (i + 1)) / (imgs.length + 1)))
  );
  paragraphs.forEach((p, i) => {
    blocks.push({ type: "text", value: p, idx: i });
    const imgIndex = points.indexOf(i);
    if (imgIndex !== -1 && imgs[imgIndex]) {
      blocks.push({ type: "image", value: imgs[imgIndex], idx: imgIndex });
    }
  });
  return blocks;
}

function NotifyMe() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/public/subscribe", { email: email.trim(), source: "chronicles" });
      setDone(true);
      toast.success("You're on the list — check your inbox to confirm.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not subscribe.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="chronicle-notify surface p-8 sm:p-10 text-center" data-testid="chronicle-notify">
      <Crown size={20} className="mx-auto mb-4" fill="var(--gold)" fillOpacity={0.25} strokeWidth={1.3} style={{ color: "var(--gold)" }} />
      <h3 className="font-serif text-2xl sm:text-3xl font-light tracking-tight mb-3">
        The story continues.
      </h3>
      <p className="text-white/60 text-sm sm:text-base max-w-md mx-auto mb-7">
        Be the first to know when the next chapter of the Chronicles drops.
        No spam — just the next page of the empire.
      </p>
      {done ? (
        <div
          className="inline-flex items-center gap-2 text-[color:var(--gold)] text-sm uppercase tracking-[0.25em]"
          data-testid="chronicle-notify-done"
        >
          <Check size={16} /> You&rsquo;re on the list
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="flex-1 bg-transparent border border-white/15 rounded-none px-4 h-12 text-sm outline-none focus:border-[color:var(--gold)] transition-colors"
            data-testid="chronicle-notify-input"
          />
          <button
            type="submit"
            disabled={busy}
            className="btn-gold rounded-none px-6 h-12 text-xs uppercase tracking-[0.25em] inline-flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="chronicle-notify-submit"
          >
            <Bell size={14} />
            {busy ? "Adding…" : "Notify Me"}
          </button>
        </form>
      )}
    </div>
  );
}

function GateOverlay({ episode, onUnlock }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/public/subscribe", { email: email.trim(), source: "chronicles-unlock" });
      try {
        localStorage.setItem(GATE_KEY, "1");
        localStorage.setItem("nc_chronicles_email", email.trim());
      } catch (err) {
        console.debug("localStorage unavailable", err);
      }
      toast.success("Unlocked — enjoy the chapter. We saved this on your device.");
      onUnlock();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not unlock.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-20 lg:py-28" data-testid="chronicle-gate">
      {episode.tagline && (
        <p className="font-serif italic text-xl sm:text-2xl text-white/70 leading-[1.5] mb-12 pl-5 border-l-2 border-[color:var(--gold)]/50">
          {episode.tagline}
        </p>
      )}
      <div className="chronicle-notify surface p-8 sm:p-12 text-center relative overflow-hidden">
        <div className="w-14 h-14 mx-auto mb-6 rounded-full border border-[color:var(--gold)]/40 flex items-center justify-center">
          <Lock size={22} style={{ color: "var(--gold)" }} strokeWidth={1.4} />
        </div>
        <p className="text-[10px] uppercase tracking-[0.4em] text-[color:var(--gold)] mb-4">
          Members-Only Chapter
        </p>
        <h3 className="font-serif text-3xl sm:text-4xl font-light tracking-tight mb-4">
          Sign up to keep reading.
        </h3>
        <p className="text-white/60 text-sm sm:text-base max-w-md mx-auto mb-8">
          Episodes beyond the opening chapters are reserved for the inner circle.
          Drop your email to unlock this chapter — and every gated one after it.
          It&rsquo;s free.
        </p>
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="flex-1 bg-transparent border border-white/15 rounded-none px-4 h-12 text-sm outline-none focus:border-[color:var(--gold)] transition-colors"
            data-testid="chronicle-gate-input"
          />
          <button
            type="submit"
            disabled={busy}
            className="btn-gold rounded-none px-6 h-12 text-xs uppercase tracking-[0.25em] inline-flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="chronicle-gate-submit"
          >
            <Crown size={14} />
            {busy ? "Unlocking…" : "Unlock & Read"}
          </button>
        </form>
        <p className="text-[11px] text-white/35 mt-5">
          Already signed up on another device? Just enter the same email.
        </p>
      </div>
    </div>
  );
}

export default function ChronicleReader() {
  const { slug } = useParams();
  const [ep, setEp] = useState(null);
  const [error, setError] = useState(null);
  const [unlocked, setUnlocked] = useState(() => {
    try {
      return localStorage.getItem(GATE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const bodyRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    let active = true;
    const id = setTimeout(() => {
      setEp(null);
      setError(null);
      api
        .get(`/public/chronicles/${slug}`)
        .then((r) => active && setEp(r.data))
        .catch(() => active && setError("Episode not found"));
    }, 0);
    return () => {
      active = false;
      clearTimeout(id);
    };
  }, [slug]);

  if (error)
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <p className="font-serif text-3xl">{error}</p>
        <Link to="/chronicles" className="link-gold inline-flex items-center gap-2 mt-6">
          <ArrowLeft size={14} /> Back to the Chronicles
        </Link>
      </div>
    );

  if (!ep)
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-white/40 font-serif italic">
        Loading…
      </div>
    );

  const blocks = buildBlocks(ep.content, ep.images);
  const firstTextIdx = blocks.findIndex((b) => b.type === "text");
  const limit = ep.free_episode_limit ?? 5;
  const isGated = ep.episode_number > limit && !unlocked;

  return (
    <article className="chronicles-bg min-h-screen" data-testid="chronicle-reader">
      {/* Cinematic full-bleed cover */}
      <div className="relative h-[72vh] min-h-[520px] w-full overflow-hidden">
        {ep.cover_image && (
          <img
            src={imgUrl(ep.cover_image)}
            alt={ep.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 chronicle-cover-veil" />
        <div className="relative h-full max-w-[1100px] mx-auto px-6 flex flex-col justify-end pb-16">
          <Link
            to="/chronicles"
            className="absolute top-28 left-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-white/70 hover:text-[color:var(--gold)] transition-colors"
            data-testid="chronicle-back"
          >
            <ArrowLeft size={14} /> The Chronicles
          </Link>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-[color:var(--gold)] mb-4">
            <Crown size={13} fill="var(--gold)" fillOpacity={0.25} strokeWidth={1.3} />
            Episode {ep.episode_number}
          </div>
          <h1 className="font-serif font-light tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl max-w-3xl">
            {ep.title}
          </h1>
          {ep.subtitle && (
            <p className="mt-5 text-sm uppercase tracking-[0.3em] text-white/55">{ep.subtitle}</p>
          )}
          <div className="mt-6 flex items-center gap-3" data-testid="chronicle-share">
            <span className="text-[10px] uppercase tracking-[0.3em] text-white/40">Share</span>
            <ShareButtons
              shareUrl={`${window.location.origin}/api/og/chronicles/${slug}`}
              canonicalUrl={`${window.location.origin}/chronicles/${slug}`}
              text={`${ep.title} — The Mob Mistress Chronicles by Nina Capone`}
              testidPrefix="chronicle-share"
            />
          </div>
        </div>
      </div>

      {/* Body — gated behind sign-up for episodes beyond the free limit */}
      {isGated ? (
        <GateOverlay episode={ep} onUnlock={() => setUnlocked(true)} />
      ) : (
      <div ref={bodyRef} className="max-w-2xl mx-auto px-6 py-20 lg:py-28">
        {ep.tagline && (
          <p className="font-serif italic text-xl sm:text-2xl text-white/70 leading-[1.5] mb-14 pl-5 border-l-2 border-[color:var(--gold)]/50">
            {ep.tagline}
          </p>
        )}

        <div className="chronicle-prose font-serif text-lg sm:text-xl text-white/85 leading-[1.7] space-y-7">
          {blocks.map((b, i) => {
            if (b.type === "image") {
              return (
                <figure
                  key={`img-${i}`}
                  className="chronicle-figure my-14 -mx-6 sm:mx-0"
                  data-testid={`chronicle-figure-${b.idx}`}
                >
                  <img
                    src={imgUrl(b.value)}
                    alt={`${ep.title} — visual ${b.idx + 1}`}
                    loading="lazy"
                    className="w-full"
                  />
                </figure>
              );
            }
            const isDropCap = i === firstTextIdx;
            return (
              <p key={`p-${i}`} className={isDropCap ? "chronicle-dropcap" : ""}>
                {renderParagraph(b.value, `p${i}`)}
              </p>
            );
          })}
        </div>

        <div className="mt-20">
          <ChronicleInteractions slug={slug} />
        </div>

        <div className="mt-20">
          <NotifyMe />
        </div>

        {/* Prev / Next */}
        <nav className="mt-16 grid sm:grid-cols-2 gap-4">
          {ep.prev ? (
            <Link
              to={`/chronicles/${ep.prev.slug}`}
              className="surface p-5 group hover:border-[color:var(--gold)]/40 transition-colors"
              data-testid="chronicle-prev"
            >
              <span className="text-[10px] uppercase tracking-[0.3em] text-white/40 inline-flex items-center gap-2">
                <ArrowLeft size={12} /> Episode {ep.prev.episode_number}
              </span>
              <p className="font-serif text-lg mt-2 group-hover:text-[color:var(--gold)] transition-colors">
                {ep.prev.title}
              </p>
            </Link>
          ) : (
            <span />
          )}
          {ep.next ? (
            <Link
              to={`/chronicles/${ep.next.slug}`}
              className="surface p-5 group hover:border-[color:var(--gold)]/40 transition-colors text-right"
              data-testid="chronicle-next"
            >
              <span className="text-[10px] uppercase tracking-[0.3em] text-white/40 inline-flex items-center gap-2 justify-end w-full">
                Episode {ep.next.episode_number} <ArrowRight size={12} />
              </span>
              <p className="font-serif text-lg mt-2 group-hover:text-[color:var(--gold)] transition-colors">
                {ep.next.title}
              </p>
            </Link>
          ) : (
            <span />
          )}
        </nav>
      </div>
      )}
    </article>
  );
}
